import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { ColumnDef } from '@tanstack/react-table';
import { ArrowRightLeft, Boxes, Eye, Plus, Trash2 } from 'lucide-react';
import { PageHeader } from '@/shared/ui/layout/PageHeader';
import { Button } from '@/shared/ui/buttons/Button';
import { IconButton } from '@/shared/ui/buttons/IconButton';
import { ErrorState } from '@/shared/ui/feedback/ErrorState';
import { DataTable } from '@/shared/ui/data-table/DataTable';
import { Pagination } from '@/shared/ui/data-table/Pagination';
import { FilterBar } from '@/shared/ui/data-table/FilterBar';
import { PartyStatusChip } from '@/shared/ui/chips/PartyStatusChip';
import { extractErrorMessage } from '@/shared/api/errors';
import { usePartiesList } from '@/entities/parties';
import { useDebouncedValue } from '@/shared/hooks/useDebouncedValue';
import { usePerm } from '@/shared/hooks/usePerm';
import {
  FeaturesPartiesCreatePartyDialog,
  FeaturesPartiesDeleteDialog,
  FeaturesPartiesStatusDialog,
} from '@/features/parties';
import { formatDate } from '@/shared/utils/date';
import type { ApiPartiesGetItem } from '@/shared/types/api/parties';

const PAGE_SIZE = 20;

export const WidgetsPartiesMain = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const canCreate = usePerm('parties.create');
  const canChangeStatus = usePerm('parties.detail.change_status');
  const canDelete = usePerm('parties.delete');

  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebouncedValue(search, 350);
  const [createOpen, setCreateOpen] = useState(false);
  const [statusTarget, setStatusTarget] = useState<ApiPartiesGetItem | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<ApiPartiesGetItem | null>(null);

  const params = useMemo(
    () => ({
      page,
      page_size: PAGE_SIZE,
      search: debouncedSearch.trim() || undefined,
    }),
    [page, debouncedSearch],
  );

  const query = usePartiesList(params);

  const columns = useMemo<ColumnDef<ApiPartiesGetItem>[]>(
    () => [
      {
        accessorKey: 'name',
        header: t('parties.list.columns.name'),
        cell: (info) => <span className="font-medium text-ink">{info.row.original.name}</span>,
      },
      {
        accessorKey: 'status',
        header: t('parties.list.columns.status'),
        cell: (info) => <PartyStatusChip status={info.row.original.status} />,
      },
      {
        accessorKey: 'packages_count',
        header: t('parties.list.columns.packages'),
        cell: (info) => <span className="font-medium">{info.row.original.packages_count}</span>,
      },
      {
        accessorKey: 'created_at',
        header: t('parties.list.columns.createdAt'),
        cell: (info) => formatDate(info.row.original.created_at),
      },
      {
        id: 'actions',
        header: t('parties.list.columns.actions'),
        enableSorting: false,
        cell: (info) => {
          const row = info.row.original;
          return (
            <div className="flex items-center gap-1.5">
              <IconButton
                label={t('parties.list.actions.view')}
                onClick={(e) => {
                  e.stopPropagation();
                  navigate(`/parties/${row.id}`);
                }}
              >
                <Eye className="h-4 w-4" />
              </IconButton>
              {canChangeStatus && (
                <IconButton
                  label={t('parties.list.actions.changeStatus')}
                  onClick={(e) => {
                    e.stopPropagation();
                    setStatusTarget(row);
                  }}
                >
                  <ArrowRightLeft className="h-4 w-4 text-brand-600" />
                </IconButton>
              )}
              {canDelete && row.status === 'COLLECTING' && (
                <IconButton
                  label={t('parties.list.actions.delete')}
                  onClick={(e) => {
                    e.stopPropagation();
                    setDeleteTarget(row);
                  }}
                >
                  <Trash2 className="h-4 w-4 text-red-600" />
                </IconButton>
              )}
            </div>
          );
        },
      },
    ],
    [navigate, canChangeStatus, canDelete, t],
  );

  return (
    <>
      <PageHeader
        title={t('parties.list.title')}
        subtitle={t('parties.list.subtitle')}
        right={
          canCreate ? (
            <Button
              leftIcon={<Plus className="h-4 w-4" />}
              onClick={() => setCreateOpen(true)}
              title={t('parties.list.newParty')}
              aria-label={t('parties.list.newParty')}
            >
              <span className="hidden sm:inline">{t('parties.list.newParty')}</span>
            </Button>
          ) : null
        }
      />
      <div className="space-y-4 px-4 py-4 lg:px-6 lg:py-6">
        <FilterBar
          searchValue={search}
          onSearchChange={(v) => {
            setPage(1);
            setSearch(v);
          }}
          searchPlaceholder={t('parties.list.searchPlaceholder')}
        />

        {query.error ? (
          <ErrorState
            message={extractErrorMessage(query.error, t('parties.list.loadError'))}
            onRetry={() => void query.refetch()}
          />
        ) : (
          <>
            <DataTable
              data={query.data?.results ?? []}
              columns={columns}
              isLoading={query.isLoading}
              emptyTitle={t('parties.list.emptyTitle')}
              emptyDescription={t('parties.list.emptyDescription')}
              emptyIcon={<Boxes className="h-8 w-8" aria-hidden />}
              onRowClick={(row) => navigate(`/parties/${row.id}`)}
              getRowId={(row) => row.id}
              numbered
              pageIndex={page - 1}
              pageSize={PAGE_SIZE}
            />
            <Pagination
              page={page}
              pageSize={PAGE_SIZE}
              total={query.data?.count ?? 0}
              onPageChange={setPage}
            />
          </>
        )}
      </div>

      <FeaturesPartiesCreatePartyDialog open={createOpen} onClose={() => setCreateOpen(false)} />
      <FeaturesPartiesStatusDialog party={statusTarget} onClose={() => setStatusTarget(null)} />
      <FeaturesPartiesDeleteDialog
        party={deleteTarget}
        redirectTo={null}
        onClose={() => setDeleteTarget(null)}
      />
    </>
  );
};
